import type { AGUAppLocation } from '../../app-surface/app-location';
import type { AGUAppModuleDefinition } from '../../app-surface/app-module-definition';

/** One step of a crumb trail: an ancestor the member can go back to, or (with no location) the page on screen. */
export interface AGUCrumbItem {
    readonly Label: string;
    /** Where the ancestor goes; null for the current page, which renders as `<b aria-current="page">`. */
    readonly Location: AGUAppLocation | null;
}

/** The Career Explorer view a "Career paths" crumb returns to (P/06_career-explorer.html, the role view). */
const CAREER_MAP_KEY = 'career-map';
const CAREER_ROLE_VIEW = 'role';
const CAREER_PATHS_LABEL = 'Career paths';

/**
 * What the crumb calls an origin: the `Title` of the module it belongs to (Grant Finder, Home, Search, Saved…), except Career
 * Explorer's role view, which reads "Career paths". Falls back to the module key when the composition does not hold the module (a
 * stale `f=` from another app's link), so the crumb never renders an empty button.
 */
export function LabelForLocation(modules: readonly AGUAppModuleDefinition[], loc: AGUAppLocation): string {
    if (loc.ModuleKey === CAREER_MAP_KEY && loc.View === CAREER_ROLE_VIEW) return CAREER_PATHS_LABEL;
    return modules.find((m) => m.Key === loc.ModuleKey)?.Title ?? loc.ModuleKey;
}

/** Where a detail view's crumb returns to: the place it was opened from, else its own module's default view. */
export function OriginOf(loc: AGUAppLocation, defaultView: string): AGUAppLocation {
    return loc.From ?? { ModuleKey: loc.ModuleKey, View: defaultView };
}

/** A detail view's two-step trail: the origin, then the record's title (P/detail.html:888). */
export function DetailTrail(originLabel: string, origin: AGUAppLocation, currentTitle: string): AGUCrumbItem[] {
    return [
        { Label: originLabel, Location: origin },
        { Label: currentTitle, Location: null },
    ];
}

/** The ancestor a phone app bar's back chevron goes to: the trail's first item with a location, or null. */
export function FirstAncestor(trail: readonly AGUCrumbItem[]): AGUCrumbItem | null {
    return trail.find((item) => item.Location !== null) ?? null;
}
